import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react'
import { Dropdown, Form, OverlayTrigger, Popover } from 'react-bootstrap' // Import OverlayTrigger and Popover
import { supabase } from '../../supabaseClient'
import { Tabs, Tab, Spinner } from 'react-bootstrap' // Import Spinner
import Select from 'react-select'

import TaskExportMorePopup from '../common/TaskExportMorePopup' // Import TaskExportMorePopup
import TaskExpandAllPopup from '../common/TaskExpandAllPopup'
import ConfirmationPopup from '../common/ConfirmationPopup' // Import ConfirmationPopup
import EditPopup from '../common/EditPopup' // Import EditPopup
import moment from 'moment-timezone'  
import DeleteCategoryPopUp from '../common/DeleteCategory'

import { DateFormtter } from '../../utils'
import { useTimezone } from "../../Context/TimezoneContext";
import {
  getLocalDayRangeUtc,
  getRelativeTaskDateInfo,
  parseStoredTaskDateTime,
} from "../../utils/taskDateTime";
const DueTaskList = forwardRef(({
  isDarkMode,
  isCategoryUpdateListTask,
  isTaskChannelUpdateListTask,
  localCategoryRefresh,
  onCategoryDeleted,
  showFilters
}, ref) => {
  const [UserId, setUserId] = useState(null)
  const [tasks, setTasks] = useState([])
  const [completedTasks, setCompletedTasks] = useState({})
  const [hasMoreTasks, setHasMoreTasks] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [categories, setCategories] = useState([])
  const [subcategories, setSubcategories] = useState([])
  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedSubcategory, setSelectedSubcategory] = useState('') // Define selectedSubcategory state
  const [showExportMore, setShowExportMore] = useState(false)
  const [showExpandAll, setShowExpandAll] = useState(false)
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [ShowDeleteCategory, setShowDeleteCategory] = useState(false)

  const [allTasks, setAllTasks] = useState([])
  const [showPopover, setShowPopover] = useState(null) // Add state to manage popover visibility
  const [showConfirmation, setShowConfirmation] = useState(false) // Add state to manage confirmation popup visibility
  const [taskToRemove, setTaskToRemove] = useState(null) // Add state to track task to be removed
  const [showEditPopup, setShowEditPopup] = useState(false) // Add state to manage edit popup visibility
  const [taskToEdit, setTaskToEdit] = useState(null) // Add state to track task to be edited
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [isLoading, setIsLoading] = useState(true)
  const [groupedTasks, setGroupedTasks] = useState({
    today: [],
    next7Days: [],
    next30Days: [],
    later: [],
    yesterDayTasks: [],
    last7Days: [],
    last30Days: [],
    previous: []
  })

  const popoverRef = useRef(null)
  const [childKey, setChildKey] = useState('today') // Default to Today tab
  const [categoryUpdateCode, setCategoryUpdateCode] = useState(null)
  const { userTimezone } = useTimezone();

  // Expose functions to parent
  useImperativeHandle(ref, () => ({
    triggerShowAll: () => {
      fetchAllTasks(searchTerm, selectedCategory, selectedSubcategory)
      setShowExportMore(true)
    },
    hasActiveFilters: () => {
      return !!(selectedCategory || searchTerm)
    }
  }));

  const fetchCategories = async () => {
    try {
      if (UserId) {
        const { data, error } = await supabase
          .from('categories')
          .select('id, name')
          .eq('user_id', UserId)
          .is('parent_category_id', null)
        if (error) {
          throw error
        }
        setCategories(data)
      }
    } catch (error) {
      console.error('Error fetching categories:', error)
    }
  }

  const fetchSubcategories = async categoryId => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('id, name')
        .eq('parent_category_id', categoryId)
      if (error) {
        throw error
      }
      setSubcategories(data)
    } catch (error) {
      console.error('Error fetching subcategories:', error)
    }
  }

  const fetchTasks = useCallback(
    async (search = '', categoryId = '', subcategoryId = '') => {
      if (UserId) {
        setIsLoading(true)
        try {
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('time_zone')
            .eq('id', UserId)
            .single()
          if (profileError) {
            throw profileError
          }
          const userTimeZone = profileData.time_zone || userTimezone
          const userCurrentTime = new Date().toISOString()
            let query = supabase
            .from('tasks')
            .select('id, title, description, status, due_date, category_id', {
              count: 'exact'
            }) // Include due_date
            .eq('user_id', UserId)
            .or('is_share.is.null,is_share.eq.false')
            .in('status', ['pending', 'Pending'])
            .not('due_date', 'is', null) // Ensure due_date is not null
            .ilike('title', `%${search}%`)
            .gte('due_date', userCurrentTime)
            .order('created_at', { ascending: false }) // Newly created tasks first
            .limit(5)

          if (subcategoryId) {
            query = query.eq('category_id', subcategoryId)
          } else if (categoryId) {
            const { data: subcategories } = await supabase
              .from('categories')
              .select('id')
              .eq('parent_category_id', categoryId)
            const subcategoryIds = subcategories.map(sub => sub.id)
            query = query.in('category_id', [categoryId, ...subcategoryIds])
          }

          const { data, error, count } = await query
          if (error) {
            throw error
          }
          // setTasks(data)
          setHasMoreTasks(count > 5)
          const completed = {}
          data.forEach(task => {
            completed[task.id] = task.status === 'completed'
          })
          setCompletedTasks(completed)

          // Group tasks by due date
          const todayTasks = []
          const next7Days = []
          const next30Days = []
          const later = []

          data.forEach(task => {
             const dueDate = parseStoredTaskDateTime(task.due_date, userTimeZone)
             const today = moment().tz(userTimeZone).startOf('day')
             if (!dueDate) {
               later.push(task)
               return
             }
            if (dueDate.isSame(today, 'day')) {
              todayTasks.push(task)
            } else if (
              dueDate.isBetween(today.clone(), today.clone().add(7, 'days'), 'day', '[]')
            ) {
              next7Days.push(task)
            } else if (
              dueDate.isBetween(today.clone(), today.clone().add(30, 'days'), 'day', '[]')
            ) {
              next30Days.push(task)
            } else {
              later.push(task)
            }
          })
          setGroupedTasks({
            today: todayTasks,
            next7Days,
            next30Days,
            later,
            yesterDayTasks:[],
            last7Days: [],
            last30Days: [],
            previous: []
          })
        } catch (error) {
          console.error('Error fetching tasks:', error)
        } finally {
          setIsLoading(false)
        }
      }
    },
    [UserId, userTimezone]
  )

  // Fetch tasks for TODAY only (entire day, regardless of current time)
  const fetchTodayTasks = useCallback(
    async (search = '', categoryId = '', subcategoryId = '') => {
      if (UserId) {
        setIsLoading(true)
        try {
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('time_zone')
            .eq('id', UserId)
            .single()
          if (profileError) {
            throw profileError
          }
          const userTimeZone = profileData.time_zone || userTimezone
          const { startUtcIso: todayStart, endUtcIso: todayEnd } = getLocalDayRangeUtc(userTimeZone)

          let query = supabase
            .from('tasks')
            .select('id, title, description, status, due_date, category_id', { count: 'exact' })
            .eq('user_id', UserId)
            .or('is_share.is.null,is_share.eq.false')
            .in('status', ['pending', 'Pending'])
            .not('due_date', 'is', null)
            .gte('due_date', todayStart)
            .lte('due_date', todayEnd)
            .ilike('title', `%${search}%`)
            .order('due_date', { ascending: true })
            .limit(20)

          if (subcategoryId) {
            query = query.eq('category_id', subcategoryId)
          } else if (categoryId) {
            const { data: subcategories } = await supabase
              .from('categories')
              .select('id')
              .eq('parent_category_id', categoryId)
            const subcategoryIds = subcategories.map(sub => sub.id)
            query = query.in('category_id', [categoryId, ...subcategoryIds])
          }

          const { data, error, count } = await query
          if (error) {
            throw error
          }

          setHasMoreTasks(count > 20)
          const completed = {}
          data.forEach(task => {
            completed[task.id] = task.status === 'completed'
          })
          setCompletedTasks(completed)

          // All tasks go into "today" group
          setGroupedTasks({
            today: data,
            next7Days: [],
            next30Days: [],
            later: [],
            yesterDayTasks: [],
            last7Days: [],
            last30Days: [],
            previous: []
          })
        } catch (error) {
          console.error('Error fetching today tasks:', error)
        } finally {
          setIsLoading(false)
        }
      }
    },
    [UserId, userTimezone]
  )

  const fetchOverDueTask = useCallback(
    async (search = '', categoryId = '', subcategoryId = '') => {
      if (UserId) {
        setIsLoading(true)
        try {
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('time_zone')
            .eq('id', UserId)
            .single()
          if (profileError) {
            throw profileError
          }

          const userTimeZone = profileData.time_zone || userTimezone
          const userCurrentTime = new Date().toISOString()
          let query = supabase
            .from('tasks')
            .select('id, title, description, status, due_date,category_id', {
              count: 'exact'
            }) // Include due_date
            .eq('user_id', UserId)
            .or('is_share.is.null,is_share.eq.false')
            .not('due_date', 'is', null) // Ensure due_date is not null
            .in('status', ['pending', 'Pending'])
            .lte('due_date', userCurrentTime) // Tasks with due_date <= current time
            .order('created_at', { ascending: false }) // Newly created tasks first

            .limit(5)
          if (subcategoryId) {
            query = query.eq('category_id', subcategoryId)
          } else if (categoryId) {
            const { data: subcategories } = await supabase
              .from('categories')
              .select('id')
              .eq('parent_category_id', categoryId)
            const subcategoryIds = subcategories.map(sub => sub.id)
            query = query.in('category_id', [categoryId, ...subcategoryIds])
          }
          const { data, error, count } = await query
          if (error) {
            throw error
          }

          // setTasks(data)
          setHasMoreTasks(count > 5)
          const completed = {}
          data.forEach(task => {
            completed[task.id] = task.status === 'completed'
          })
          setCompletedTasks(completed)

          // Group tasks by due date
          const last7Days = []
          const last30Days = []
          const previous = []
          const todayTasks = []
          const yesterDayTasks = []

            data.forEach(task => {
            // Convert due_date to user's timezone
            const dueDate = parseStoredTaskDateTime(task.due_date, userTimeZone)
            if (!dueDate) {
              previous.push(task)
              return
            }

            // Get today's date in the user's timezone (without time)
            const today = moment().tz(userTimeZone).startOf('day')

            if (dueDate.isSame(today, 'day')) {
              todayTasks.push(task) // ✅ Task is exactly today
            } else if (dueDate.isSame(today.clone().subtract(1, 'day'), 'day')) {
              yesterDayTasks.push(task) // ✅ Task is exactly yesterday
            } else if (
              dueDate.isBetween(
              today.clone().subtract(7, 'days'), // 7 days ago
              today.clone().subtract(2, 'days').endOf('day'), // End of 2 days ago
              'day',
              '[]'
              )
            ) {
              last7Days.push(task)
            } else if (
              dueDate.isBetween(
              today.clone().subtract(30, 'days'), // 30 days ago
              today.clone().subtract(7, 'days').endOf('day'), // End of 7 days ago
              'day',
              '[]'
              )
            ) {
              last30Days.push(task)
            } else {
              previous.push(task)
            }
            })

          setGroupedTasks({
            today: todayTasks,
            next7Days: [],
            next30Days: [],
            later: [],
            yesterDayTasks,
            last7Days,
            last30Days,
            previous
          })
        } catch (error) {
          console.error('Error fetching tasks:', error)
        } finally {
          setIsLoading(false)
        }
      }
    },
    [UserId, userTimezone]
  )
  const fetchAllTasks = async (
    search = '',
    categoryId = '',
    subcategoryId = ''
  ) => {
    try {
      let query = supabase
        .from('tasks')
        .select('id, title, description, status')
        .eq('user_id', UserId)
        .order('status', { ascending: false }) // Order by created_at in descending order
        .ilike('title', `%${search}%`)

      if (categoryId) {
        query = query.eq('category_id', categoryId)
      }

      if (subcategoryId) {
        query = query.eq('category_id', subcategoryId)
      }

      const { data, error } = await query
      if (error) {
        throw error
      }
      setAllTasks(data)
    } catch (error) {
      console.error('Error fetching all tasks:', error)
    }
  }

  useEffect(() => {
    // FIX: Only fetch tasks and categories when UserId is available
    // This prevents race condition where fetchTasks is called before auth completes
    if (!UserId) {
      return // Wait for UserId to be set by auth useEffect
    }

    fetchCategories()
    fetchTodayTasks() // Default to Today tab

    // Note: Real-time updates are handled by the main broadcasting system in App.jsx
    // which increments taskChannel when tasks change. This useEffect listens to that.
    // No local subscription needed - use the parent-provided isTaskChannelUpdateListTask

    return () => {
      // Cleanup if needed
    }
  }, [UserId, fetchTodayTasks])

  useEffect(() => {
    fetchCategories()
    if (categoryUpdateCode !== null) {
      fetchSubcategories(categoryUpdateCode)
    }
  }, [isCategoryUpdateListTask])

  // Refresh categories when localCategoryRefresh changes (triggered by sibling tab)
  useEffect(() => {
    if (localCategoryRefresh > 0) {
      fetchCategories()
    }
  }, [localCategoryRefresh])

  useEffect(() => {
    if (showExportMore) {
      fetchAllTasks(searchTerm, selectedCategory, selectedSubcategory)
    }
  }, [
    showExportMore,
    fetchAllTasks,
    searchTerm,
    selectedCategory,
    selectedSubcategory
  ])

  const handleCategoryChange = e => {
    const categoryId = e.target.value
    setCategoryUpdateCode(categoryId)
    setSelectedCategory(categoryId)
    fetchSubcategories(categoryId)
    if (childKey === 'today') {
      fetchTodayTasks(searchTerm, categoryId)
    } else if (childKey === 'overDue') {
      fetchOverDueTask(searchTerm, categoryId)
    } else {
      fetchTasks(searchTerm, categoryId)
    }
  }

  const handleSubcategoryChange = e => {
    const subcategoryId = e.target.value
    setSelectedSubcategory(subcategoryId)
    if (childKey === 'today') {
      fetchTodayTasks(searchTerm, selectedCategory, subcategoryId)
    } else if (childKey === 'overDue') {
      fetchOverDueTask(searchTerm, selectedCategory, subcategoryId)
    } else {
      fetchTasks(searchTerm, selectedCategory, subcategoryId)
    }
  }

  const handleSearchChange = e => {
    const search = e.target.value
    setSearchTerm(search)
    if (childKey === 'today') {
      fetchTodayTasks(search, selectedCategory, selectedSubcategory)
    } else if (childKey === 'overDue') {
      fetchOverDueTask(search, selectedCategory, selectedSubcategory)
    } else {
      fetchTasks(search, selectedCategory, selectedSubcategory)
    }
  }

  const toggleTaskCompletion = async taskId => {
    const newStatus = completedTasks[taskId] ? 'pending' : 'completed'
    try {
      const { error } = await supabase
        .from('tasks')
        .update({ status: newStatus })
        .eq('id', taskId)
        .eq('user_id', UserId)
      if (error) {
        throw error
      }
      setCompletedTasks(prev => ({
        ...prev,
        [taskId]: !prev[taskId]
      }))
    } catch (error) {
      console.error('Error updating task status:', error)
    }
  }

  const confirmRemoveTask = taskId => {
    setTaskToRemove(taskId)
    setShowConfirmation(true)
  }

  const removeTask = async () => {
    try {
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', taskToRemove)
        .eq('user_id', UserId)
      if (error) {
        throw error
      }
      setTasks(prev => prev.filter(task => task.id !== taskToRemove))
      setShowConfirmation(false)
      setTaskToRemove(null)
    } catch (error) {
      console.error('Error removing task:', error)
    }
  }

  const handleClickOutside = event => {
    if (popoverRef.current && !popoverRef.current.contains(event.target)) {
      setShowPopover(null)
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleEditTask = task => {
    setTaskToEdit(task)
    setShowEditPopup(true)
  }

  const saveTask = async updatedTask => {
    try {
      let categoryId = updatedTask.category_id

      // Handle new category creation
      if (updatedTask.isNewCategory && updatedTask.category_id) {
        const { data: catData, error: catError } = await supabase
          .from('categories')
          .insert([{
            name: updatedTask.category_id,
            parent_category_id: null,
            user_id: UserId
          }])
          .select()

        if (catError) {
          console.error('Error creating category:', catError)
          throw catError
        }
        categoryId = catData[0].id

        // Handle new subcategory creation (requires new category to be created first)
        if (updatedTask.isNewSubcategory && updatedTask.subcategory_id) {
          const { data: subData, error: subError } = await supabase
            .from('categories')
            .insert([{
              name: updatedTask.subcategory_id,
              parent_category_id: categoryId,
              user_id: UserId
            }])
            .select()

          if (subError) {
            console.error('Error creating subcategory:', subError)
            throw subError
          }
          categoryId = subData[0].id
        }
      } else if (updatedTask.isNewSubcategory && updatedTask.subcategory_id) {
        // Creating new subcategory under existing category
        const parentCategoryId = updatedTask.category_id
        const { data: subData, error: subError } = await supabase
          .from('categories')
          .insert([{
            name: updatedTask.subcategory_id,
            parent_category_id: parentCategoryId,
            user_id: UserId
          }])
          .select()

        if (subError) {
          console.error('Error creating subcategory:', subError)
          throw subError
        }
        categoryId = subData[0].id
      } else if (updatedTask.subcategory_id && /^\d+$/.test(updatedTask.subcategory_id)) {
        // Using existing subcategory - use subcategory ID as category_id
        categoryId = updatedTask.subcategory_id
      }

      const updateData = {
        title: updatedTask.title,
        description: updatedTask.description
      }

      // Only update category_id if it's a valid ID (not "0" for General)
      // When "0" (General), don't touch category_id to preserve existing value
      if (categoryId && categoryId !== "0") {
        updateData.category_id = categoryId
      }

      if (updatedTask.due_date) {
        updateData.due_date = updatedTask.due_date
      }

      const { error } = await supabase
        .from('tasks')
        .update(updateData)
        .eq('id', updatedTask.id)
        .eq('user_id', UserId)
      if (error) {
        throw error
      }

      // Refresh categories if new ones were created
      if (updatedTask.isNewCategory || updatedTask.isNewSubcategory) {
        fetchCategories()
        if (selectedCategory) {
          fetchSubcategories(selectedCategory)
        }
      }

      setTasks(prev =>
        prev.map(task => (task.id === updatedTask.id ? { ...updatedTask, category_id: categoryId } : task))
      )
      setShowEditPopup(false)
      setTaskToEdit(null)
    } catch (error) {
      console.error('Error updating task:', error)
    }
  }

  const handleExpandAll = () => {
    fetchAllTasks(searchTerm, selectedCategory, selectedSubcategory)
    setShowExpandAll(true)
  }
  const formatMessage = text => {
    if (!text) return '';
 let cleanedText = text
    .replace(/^"/, '') // Remove leading quotation mark
    .replace(/"$/, '') // Remove trailing quotation mark
    .replace(/\\n/g, '') // Remove all escaped newlines
    return cleanedText
      .replace(/###\s*(.*?)(?:\n|$)/g, '<h5>$1</h5>') // H3 headings
      .replace(/##\s*(.*?)(?:\n|$)/g, '<h4>$1</h4>') // H2 headings
      .replace(/#\s*(.*?)(?:\n|$)/g, '<h3>$1</h3>') // H1 headings
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold text
      .replace(/(\d+)\.\s+(.*?)(?:\n|$)/g, '<ol start="$1"><li>$2</li></ol>') // Numbered lists
      .replace(
        /`(.*?)`/g,
        '<code style="background-color: #f0f0f0; padding: 2px 4px; border-radius: 4px; font-family: monospace;">$1</code>'
      ) // Inline code
      .replace(
        /\[(.*?)\]\((https?:\/\/[^\s]+)\)/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
      )
      .replace(/(^|[^"'>])(https?:\/\/[^\s<]+)/g,
        '$1<a href="$2" target="_blank" rel="noopener noreferrer">$2</a>'
      )
      // .replace(/\n/g, '<br />') // New lines
      .replace(/\{(.*?)\}/g, '$1') // Remove curly braces
  }

  // Helper function to get relative date display
  const getRelativeDateInfo = (dueDate) => {
    return getRelativeTaskDateInfo(dueDate, userTimezone, { includeTime: true });
  };

  const renderTask = (taskId, title, description, due_date, category_id) => {
    const isCompleted = completedTasks[taskId];
    const dateInfo = getRelativeDateInfo(due_date);

    return (
      <div
        className={`task-card ${isCompleted ? 'task-completed' : ''}`}
        key={taskId}
        onClick={e => {
          if (openDropdownId === taskId) return;
          handleEditTask({ id: taskId, title, description, due_date, category_id });
        }}
      >
        {/* Task Header Row */}
        <div className="task-card-header">
          <div
            className={`task-checkbox ${isCompleted ? 'checked' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              toggleTaskCompletion(taskId);
            }}
          >
            {isCompleted && <i className="bi bi-check"></i>}
          </div>

          <div className="task-card-content">
            <h4 className={`task-title ${isCompleted ? 'completed' : ''}`} title={title}>
              {title}
            </h4>
            {description && (
              <p
                className={`task-description ${isCompleted ? 'completed' : ''}`}
                dangerouslySetInnerHTML={{ __html: formatMessage(description) }}
              />
            )}
          </div>

          <Dropdown
            className="task-menu"
            align='end'
            show={openDropdownId === taskId}
            onToggle={isOpen => setOpenDropdownId(isOpen ? taskId : null)}
          >
            <Dropdown.Toggle
              variant='link'
              bsPrefix='p-0'
              id={`dropdown-${taskId}`}
              onClick={e => {
                e.stopPropagation();
                setOpenDropdownId(taskId);
              }}
            >
              <i
                className='bi bi-three-dots-vertical'
                onMouseDown={(e) => e.stopPropagation()}
              ></i>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                onClick={e => {
                  e.stopPropagation();
                  setOpenDropdownId(null);
                  handleEditTask({ id: taskId, title, description, due_date, category_id });
                }}
              >
                <i className="bi bi-pencil me-2"></i>Edit
              </Dropdown.Item>
              <Dropdown.Item
                className='text-danger'
                onClick={e => {
                  e.stopPropagation();
                  setOpenDropdownId(null);
                  confirmRemoveTask(taskId);
                }}
              >
                <i className="bi bi-trash me-2"></i>Remove
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>

        {/* Task Footer Row */}
        <div className="task-card-footer">
          <div className={`task-status-badge ${isCompleted ? 'status-completed' : 'status-pending'}`}>
            <i className={`bi ${isCompleted ? 'bi-check-circle-fill' : 'bi-clock'}`}></i>
            <span>{isCompleted ? 'Completed' : 'Pending'}</span>
          </div>

          {due_date && (
            <div className={`task-due-date ${dateInfo.className}`} title={DateFormtter(due_date, userTimezone)}>
              <i className={`bi ${dateInfo.icon}`}></i>
              <span>{dateInfo.text}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  const filteredTasks = tasks.filter(
    task =>
      task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.description.toLowerCase().includes(searchTerm.toLowerCase())
  )
  useEffect(() => {
    let getUser = async () => {
      const {
        data: { user }
      } = await supabase.auth.getUser()
      setUserId(user?user.id:null)
    }
    getUser()
  }, [])

  const handleChildKey = k => {

    if (k !== childKey) {
      setChildKey(k)
      if (k === 'today') {
        fetchTodayTasks(searchTerm, selectedCategory, selectedSubcategory)
      } else if (k === 'overDue') {
        fetchOverDueTask(searchTerm, selectedCategory, selectedSubcategory)
      } else {
        fetchTasks(searchTerm, selectedCategory, selectedSubcategory)
      }
    }
  }
  useEffect(() => {
    if (childKey === 'today') {
      fetchTodayTasks(searchTerm, selectedCategory, selectedSubcategory)
    } else if (childKey === 'overDue') {
      fetchOverDueTask(searchTerm, selectedCategory, selectedSubcategory)
    } else {
      fetchTasks(searchTerm, selectedCategory, selectedSubcategory)
    }
  }, [isTaskChannelUpdateListTask])

  return (
    <div>
      {showFilters && (
        <>
          <div className='d-flex mb-3 gap-2 align-items-center' style={{ padding: "0em 1.7em" }}>
            <Select
              className='custom-select-container'
              classNamePrefix='custom-select'
              options={[
                { value: '', label: 'All Categories' },
                { value: '0', label: 'General' },
                ...categories.map(category => ({
                  value: category.id,
                  label: category.name
                }))
              ]}
              defaultValue={{ value: '', label: 'All Categories' }}
              onChange={(option) => handleCategoryChange({ target: { value: option?.value || '' } })}
              isSearchable={false}
              placeholder="All Categories"
            />
            <Select
              className='custom-select-container'
              classNamePrefix='custom-select'
              options={[
                { value: '', label: 'All Subcategories' },
                ...subcategories.map(subcategory => ({
                  value: subcategory.id,
                  label: subcategory.name
                }))
              ]}
              defaultValue={{ value: '', label: 'All Subcategories' }}
              onChange={(option) => handleSubcategoryChange({ target: { value: option?.value || '' } })}
              isSearchable={false}
              isDisabled={!selectedCategory}
              placeholder="All Subcategories"
            />
            {categories?.length > 0 && (
              <button
                className='delete-category-btn'
                style={{
                  backgroundColor: "var(--background)",
                  color: "var(--text)",
                  borderRadius: "8px",
                  padding: "8px 10px",
                  border: "1px solid var(--border-color)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s ease"
                }}
                onClick={() => setShowDeleteCategory(true)}
              >
                <i className='bi bi-trash'></i>
              </button>
            )}
          </div>
          <div style={{ padding: "0em 1.7em" }}>
            <Form.Control
              type='text'
              placeholder='Search...'
              className='mb-3 search-input-box'
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
        </>
      )}
      <hr />

      <Tabs
        id='checklist-tabs'
        activeKey={childKey}
        onSelect={handleChildKey}
        className='mb-3 nested-tabs'
      >
        <Tab eventKey='today' title='Today'></Tab>
        <Tab eventKey='upcoming' title='Upcoming'></Tab>
        <Tab eventKey='overDue' title='OverDue'></Tab>
      </Tabs>
      <hr />
      <div className='d-flex flex-column'>
      <div className='task-list' style={{ padding: "0.6em 1.5em" }}>
        {isLoading ? (
          <div className="wave-loader-container">
            <div className="wave-loader">
              <div className="wave-bar"></div>
              <div className="wave-bar"></div>
              <div className="wave-bar"></div>
              <div className="wave-bar"></div>
              <div className="wave-bar"></div>
            </div>
            <span className="wave-loader-text">Loading due tasks...</span>
          </div>
        ) : (
          <>
            {groupedTasks.today?.length > 0 && (
              <>
                <h5>Today</h5>
                {groupedTasks.today.map(task =>
                  renderTask(task.id, task.title, task.description, task.due_date, task.category_id)
                )}
              </>
            )}
            {groupedTasks.next7Days?.length > 0 && (
              <>
                <h5>Next 7 Days</h5>
                {groupedTasks.next7Days.map(task =>
                  renderTask(task.id, task.title, task.description, task.due_date, task.category_id)
                )}
              </>
            )}
            {groupedTasks.next30Days?.length > 0 && (
              <>
                <h5>Next 30 Days</h5>
                {groupedTasks.next30Days.map(task =>
                  renderTask(task.id, task.title, task.description, task.due_date, task.category_id)
                )}
              </>
            )}
            {groupedTasks.later?.length > 0 && (
              <>
                <h5>Later</h5>
                {groupedTasks.later.map(task =>
                  renderTask(task.id, task.title, task.description, task.due_date, task.category_id)
                )}
              </>
            )}
            {groupedTasks.yesterDayTasks?.length > 0 && (
              <>
                <h5>Yesterday</h5>
                {groupedTasks.yesterDayTasks.map(task =>
                  renderTask(task.id, task.title, task.description, task.due_date, task.category_id)
                )}
              </>
            )}
            {groupedTasks.last7Days?.length > 0 && (
              <>
                <h5>Last 7 Days</h5>
                {groupedTasks.last7Days.map(task =>
                  renderTask(task.id, task.title, task.description, task.due_date, task.category_id)
                )}
              </>
            )}
            {groupedTasks.last30Days?.length > 0 && (
              <>
                <h5>Last 30 Days</h5>
                {groupedTasks.last30Days.map(task =>
                  renderTask(task.id, task.title, task.description, task.due_date, task.category_id)
                )}
              </>
            )}
            {groupedTasks.previous?.length > 0 && (
              <>
                <h5>Previous</h5>
                {groupedTasks.previous.map(task =>
                  renderTask(task.id, task.title, task.description, task.due_date, task.category_id)
                )}
              </>
            )}
          </>
        )}
</div>
        {(groupedTasks.today.length > 0 ||
          groupedTasks.next7Days.length > 0 ||
          groupedTasks.next30Days.length > 0 ||
          groupedTasks.later.length > 0 ||
          groupedTasks.last7Days.length > 0 ||
          groupedTasks.last30Days.length > 0 ||
          groupedTasks.previous.length > 0 ||
          hasMoreTasks) && (
          <div className='d-flex justify-content-center mt-3'>
            {/* {(groupedTasks.today.length > 0 ||
              groupedTasks.next7Days.length > 0 ||
              groupedTasks.next30Days.length > 0 ||
              groupedTasks.later.length > 0 ||
              groupedTasks.last7Days.length > 0 ||
              groupedTasks.last30Days.length > 0 ||
              groupedTasks.previous.length > 0) && ( */}
              <span
                style={{ cursor: 'pointer', marginRight: '15px' }}
                onClick={handleExpandAll}
              >
                Expand
              </span>
            {/* )}  */}
            {/* {hasMoreTasks && (   */}
              <span
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  fetchAllTasks(searchTerm, selectedCategory)
                  setShowExportMore(true)
                }}
              >
                Show All
              </span>
            {/* )} */}
          </div>
        )}
        {!(groupedTasks.today.length > 0 ||
          groupedTasks.next7Days.length > 0 ||
          groupedTasks.next30Days.length > 0 ||
          groupedTasks.later.length > 0 ||
          groupedTasks.last7Days.length > 0 ||
          groupedTasks.last30Days.length > 0 ||
          groupedTasks.previous.length > 0 ||
          hasMoreTasks) && (
          <div className='text-center'>
            <p>No data found</p>
            <hr />
            <div className='d-flex justify-content-center mt-3'>
            {
              <span
                style={{ cursor: 'pointer', marginRight: '15px' }}
                onClick={handleExpandAll}
              >
                Expand
              </span>
            }
            {
              <span
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  fetchAllTasks(searchTerm, selectedCategory)
                  setShowExportMore(true)
                }}
              >
                Show All
              </span>
            }
          </div>
          </div>
        )}
      </div>
      {UserId && (
        <TaskExportMorePopup
          show={showExportMore}
          handleClose={() => setShowExportMore(false)}
          title='Due Tasks'
          items={allTasks}
          category={selectedCategory}
          subcategory={selectedSubcategory}
          search={searchTerm}
          keyTab={'dueTaskList'}
          childKeyTab={childKey}
          UserId={UserId}
          isCategoryUpdateListTaskPopup={isCategoryUpdateListTask}
          isTaskChannelUpdateListTaskPopup={isTaskChannelUpdateListTask}
        />
      )}

      {UserId && (
        <TaskExpandAllPopup
          isDarkMode={isDarkMode}
          show={showExpandAll}
          handleClose={() => setShowExpandAll(false)}
          title='Due Tasks'
          category={selectedCategory}
          subcategory={selectedSubcategory}
          search={searchTerm}
          keyTab={'dueTaskList'}
          childKeyTab={childKey}
          UserId={UserId}
          isCategoryUpdateListTaskPopup={isCategoryUpdateListTask}
          isTaskChannelUpdateListTaskPopup={isTaskChannelUpdateListTask}
        />
      )}
      <ConfirmationPopup
        show={showConfirmation}
        handleClose={() => setShowConfirmation(false)}
        title='Confirm Removal'
        body='Are you sure you want to remove this task? This action cannot be undone.'
        handleConfirm={removeTask}
      />
      <EditPopup
        showDueDate={true} // Only pass this in the file where you want the Due Date
        show={showEditPopup}
        handleClose={() => setShowEditPopup(false)}
        handleSave={saveTask}
        item={taskToEdit}
        categoriesData={categories}
        fetchSubcategories={fetchSubcategories}
        subcategoriesData={subcategories}
        titleAction='Tasks'
        handleDelete={confirmRemoveTask}
      />
      {categories?.length > 0 && (
        <DeleteCategoryPopUp
          show={ShowDeleteCategory}
          categpries={categories}
          handleClose={() => setShowDeleteCategory(false)}
          type='tasks'
          onCategoryDeleted={() => {
            fetchCategories()
            if (onCategoryDeleted) onCategoryDeleted()
          }}
        />
      )}

    </div>
  )
});

export default DueTaskList
